import { ChatChannelIds, ChatMessageRelationType, ChatMessage as ChatMessageType, ClientType } from '@core/types';
import { Delete, MenuItem, Reply } from '@core/ui';
import { deleteChatMessage, joinChatChannel } from '@online-webinar/store';
import { useAppDispatch } from '@online-webinar/store/src/hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OpenChatMessageMenu } from '../../../types/components/OpenChatMessageMenu';
import { ChatOptionItem } from '../../atoms';

export const ChatMessageMenu = ({
    message,
    onClose,
    setRelatedMessage,
    setRelationType,
    setActiveChannel
}: {
    message: OpenChatMessageMenu | null;
    onClose: () => void;
    classNames?: string;
    setRelatedMessage: (message: ChatMessageType | null) => void;
    setRelationType: (type: ChatMessageRelationType | null) => void;
    setActiveChannel: (channelId: string) => void;
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const optionMenuContent = useRef<HTMLElement>(null);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState(message?.position || { x: 0, y: 0, height: 0 });
    const previousPosition = useRef({ x: 0, y: 0, height: 0 });

    useEffect(() => {
        setPosition(message?.position || { x: 0, y: 0, height: 0 });
        previousPosition.current = { ...(message?.position || { x: 0, y: 0, height: 0 }) };
    }, [message]);

    useEffect(() => {
        if (!message) {
            return;
        }

        document.addEventListener('click', handleToggleOptionMenuToggle, true);
        return () => {
            document.removeEventListener('click', handleToggleOptionMenuToggle, true);
        };
    }, [message]);

    const handleToggleOptionMenuToggle = (e: MouseEvent) => {
        if (!message) {
            return;
        }

        if (e.target === message.parent || (message.parent && message.parent.contains(e.target as Node))) {
            return;
        }

        const element = e.target as HTMLElement;
        if (!element) {
            return;
        }

        const isCloseClicked: boolean = typeof element.className === 'string' && element.className.includes('close');
        if (isCloseClicked || (optionMenuContent.current && !optionMenuContent.current.contains(element))) {
            onClose();
        }
    };

    if (!message) {
        return null;
    }

    const deleteMessage = () => {
        if (!message || !message.message) {
            return;
        }
        dispatch(deleteChatMessage({ channelId: message.channelId, messageId: message.message.messageId }));
    };

    const joinChannel = () => {
        if (!message || !message.message) {
            return;
        }
        dispatch(joinChatChannel(message.message.senderParticipantId));
    };

    const forwardMessage = (channelId: string, message: ChatMessageType) => {
        setRelatedMessage(message);
        setRelationType(ChatMessageRelationType.forward);

        // Reset related message after a short delay so the same message can be forwarded multiple times
        setTimeout(() => {
            setRelatedMessage(null);
            setRelationType(null);
        }, 50);

        setActiveChannel(channelId);
        onClose();
    };

    const forwardMessageToPresenter = () => {
        if (!message || !message.message) {
            return;
        }
        forwardMessage(ChatChannelIds.presenterChannel, message.message);
    };

    const forwardOriginalMessageToPresenter = () => {
        if (!message || !message.message || !message.message.relatedMessage) {
            return;
        }
        forwardMessage(ChatChannelIds.presenterChannel, message.message.relatedMessage);
    };

    const forwardMessageToModerator = () => {
        if (!message || !message.message) {
            return;
        }
        forwardMessage(ChatChannelIds.moderatorChannel, message.message);
    };

    const forwardOriginalMessageToModerator = () => {
        if (!message || !message.message || !message.message.relatedMessage) {
            return;
        }
        forwardMessage(ChatChannelIds.moderatorChannel, message.message.relatedMessage);
    };

    const forwardMessageToPublic = () => {
        if (!message || !message.message) {
            return;
        }
        forwardMessage(ChatChannelIds.publicChannel, message.message);
    };

    const forwardOriginalMessageToPublic = () => {
        if (!message || !message.message || !message.message.relatedMessage) {
            return;
        }
        forwardMessage(ChatChannelIds.publicChannel, message.message.relatedMessage);
    };

    useEffect(() => {
        const chatMenu = optionMenuContent.current;
        if (!chatMenu) return;

        const chatMenuDimension = chatMenu.getBoundingClientRect();
        const chatMenuWidth = chatMenuDimension.width;
        const chatMenuHeight = chatMenuDimension.height;
        const spacing = 4;

        const horizontalSpace = position.x + chatMenuWidth + spacing; // The size of the chat menu plus spacing and the x position of the button
        const tooFarHor = horizontalSpace > globalThis.window.innerWidth; // Check if the horizontal space exceeds the width of the screen

        const x = tooFarHor
            ? position.x - (horizontalSpace - globalThis.window.innerWidth) // Substract the overflowing horizontal space from the x position of the button
            : position.x; // Use the x position of the button to position the chat menu

        const availableSpaceBelow = globalThis.window.innerHeight - (position.y + position.height); // How much space is available below the chat options button
        const shouldOpenAbove = chatMenuHeight + spacing > availableSpaceBelow; // Check if the height of the chat menu + 4px spacing exceeds the available space

        const y = shouldOpenAbove
            ? position.y - chatMenuHeight - spacing // Open the menu above the button
            : position.y + position.height + spacing; // Open the menu below the button

        setMenuPosition({ x, y });
    }, [position]);

    useEffect(() => {
        const timer = setInterval(() => {
            if (!message || !message.parent) {
                return;
            }

            const boundingRect = message.parent.getBoundingClientRect();
            if (
                previousPosition.current.x === boundingRect.x &&
                previousPosition.current.y === boundingRect.y &&
                previousPosition.current.height === boundingRect.height
            ) {
                // If the position hasn't changed, we don't need to update the menu position
                return;
            }

            if (!boundingRect || boundingRect.height === 0) {
                onClose();
                return;
            }

            if (boundingRect.y > globalThis.window.innerHeight) {
                onClose();
                return;
            }

            setPosition({
                x: boundingRect.x,
                y: boundingRect.y,
                height: boundingRect.height
            });
            previousPosition.current = {
                x: boundingRect.x,
                y: boundingRect.y,
                height: boundingRect.height
            };
        }, 100);
        return () => clearInterval(timer);
    }, [parent]);

    return (
        <div className="z-30" style={{ position: 'absolute', left: menuPosition.x, top: menuPosition.y }} onClick={(e) => e.stopPropagation()}>
            <nav className={`min-w-48 max-w-70 bg-white rounded-md shadow-xl`} ref={optionMenuContent}>
                <ul className="py-2">
                    {/** Calling "delete" again will permanently delete the message */}
                    {message &&
                        message.message &&
                        message.message.isDeleted &&
                        (message.me?.clientType === ClientType.moderator || message.me?.clientType === ClientType.presenter) && (
                            <MenuItem>
                                <ChatOptionItem optionName={t('Chat.Option.Delete')} onClick={deleteMessage}>
                                    <Delete classNames="size-6 flex-none mr-2 -ml-1 stroke-current" />
                                </ChatOptionItem>
                            </MenuItem>
                        )}
                    {message && message.message && !message.message.isDeleted && (
                        <>
                            <MenuItem>
                                {message.message.senderParticipantId !== message.channelId &&
                                    message.me?.participantId !== message.message.senderParticipantId && (
                                        <ChatOptionItem optionName={t('Chat.Option.ReplyPrivately')} onClick={joinChannel}>
                                            <Reply classNames="size-6 flex-none mr-2 -ml-1 stroke-current" />
                                        </ChatOptionItem>
                                    )}
                                {message.channelId !== ChatChannelIds.presenterChannel &&
                                    (message.me?.clientType === ClientType.moderator || message.me?.clientType === ClientType.presenter) && (
                                        <ChatOptionItem optionName={t('Chat.Option.ForwardToPresenter')} onClick={forwardMessageToPresenter}>
                                            <Reply classNames="size-6 flex-none mr-2 -ml-1 stroke-current -scale-x-100" />
                                        </ChatOptionItem>
                                    )}
                                {message.channelId !== ChatChannelIds.presenterChannel &&
                                    message.message.relatedMessage &&
                                    (message.me?.clientType === ClientType.moderator || message.me?.clientType === ClientType.presenter) && (
                                        <ChatOptionItem optionName={t('Chat.Option.OriginalForwardToPresenter')} onClick={forwardOriginalMessageToPresenter}>
                                            <Reply classNames="size-6 flex-none mr-2 -ml-1 stroke-current -scale-x-100" />
                                        </ChatOptionItem>
                                    )}
                                {message.channelId !== ChatChannelIds.moderatorChannel && message.me?.clientType === ClientType.moderator && (
                                    <ChatOptionItem optionName={t('Chat.Option.ForwardToModerator')} onClick={forwardMessageToModerator}>
                                        <Reply classNames="size-6 flex-none mr-2 -ml-1 stroke-current -scale-x-100" />
                                    </ChatOptionItem>
                                )}
                                {message.channelId !== ChatChannelIds.moderatorChannel &&
                                    message.message.relatedMessage &&
                                    message.me?.clientType === ClientType.moderator && (
                                        <ChatOptionItem optionName={t('Chat.Option.OriginalForwardToModerator')} onClick={forwardOriginalMessageToModerator}>
                                            <Reply classNames="size-6 flex-none mr-2 -ml-1 stroke-current -scale-x-100" />
                                        </ChatOptionItem>
                                    )}
                                {message.channelId !== ChatChannelIds.moderatorChannel &&
                                    (message.me?.clientType === ClientType.moderator || message.me?.clientType === ClientType.presenter) && (
                                        <ChatOptionItem optionName={t('Chat.Option.ForwardToPublic')} onClick={forwardMessageToPublic}>
                                            <Reply classNames="size-6 flex-none mr-2 -ml-1 stroke-current -scale-x-100" />
                                        </ChatOptionItem>
                                    )}
                                {message.channelId !== ChatChannelIds.moderatorChannel &&
                                    message.message.relatedMessage &&
                                    (message.me?.clientType === ClientType.moderator || message.me?.clientType === ClientType.presenter) && (
                                        <ChatOptionItem optionName={t('Chat.Option.OriginalForwardToPublic')} onClick={forwardOriginalMessageToPublic}>
                                            <Reply classNames="size-6 flex-none mr-2 -ml-1 stroke-current -scale-x-100" />
                                        </ChatOptionItem>
                                    )}
                                {(message.me?.participantId === message.message.senderParticipantId ||
                                    message.me?.clientType === ClientType.moderator ||
                                    message.me?.clientType === ClientType.presenter) && (
                                    <ChatOptionItem optionName={t('Chat.Option.Delete')} onClick={deleteMessage}>
                                        <Delete classNames="size-6 flex-none mr-2 -ml-1 stroke-current" />
                                    </ChatOptionItem>
                                )}
                            </MenuItem>
                        </>
                    )}
                </ul>
            </nav>
        </div>
    );
};
